import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Navigate } from 'react-router-dom';
import { loginRequest } from '../store/auth/actions';
import { RootState } from '../store/reducers';
import { Eye, EyeOff } from 'lucide-react';
import logoCndj from '../logo-cndj.png';
import './Auth.css';

const Auth: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, errors, isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  // Redirect if already authenticated — ne pas rendre le formulaire
  if (isAuthenticated && user) {
    const roles = (user.role || []).map((r: any) => r.name);
    const isAdmin = roles.includes('admin') || roles.includes('superadmin');
    return <Navigate to={isAdmin ? '/espace-admin' : '/espace-cndj'} replace />;
  }

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(loginRequest({ email, password }, navigate));
  };

  return (
    <div className="auth-page">
      {/* Left panel - branding */}
      <div className="auth-left-panel">
        <div className="auth-left-content">
          <div className="auth-logo">
            <img src={logoCndj} alt="CNDJ" className="auth-logo-img" />
            <span className="auth-logo-text">Anonymisation CNDJ</span>
          </div>
          <h2 className="auth-left-title">
            Plateforme d'anonymisation des décisions de justice
          </h2>
          <p className="auth-left-description">
            Accédez à l'outil d'anonymisation automatique des décisions de justice
            du Conseil National de la Documentation Juridique. Protégez les données
            personnelles tout en préservant la transparence judiciaire.
          </p>
          <div className="auth-left-features">
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div>
                <strong>Anonymisation automatique</strong>
                <p>Détection et masquage automatique des données personnelles</p>
              </div>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div>
                <strong>Conformité RGPD</strong>
                <p>Respect des normes de protection des données personnelles</p>
              </div>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <div>
                <strong>Traitement par lots</strong>
                <p>Traitez plusieurs décisions de justice simultanément</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="auth-right-panel">
        <div className="auth-form-wrapper">
          <div className="auth-form-header">
            <h1 className="auth-form-title">Se connecter</h1>
            <p className="auth-form-subtitle">
              Entrez vos identifiants pour accéder à votre espace
            </p>
          </div>

          {errors && (
            <div className="auth-error-message">
              {typeof errors === 'string' ? errors : 'Une erreur est survenue'}
            </div>
          )}

          <form onSubmit={handleLogin} className="auth-form">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Mot de passe</label>
              <div className="input-password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Chargement...' : 'Se connecter'}
            </button>
          </form>

          <p className="auth-admin-note">
            Contactez votre administrateur pour obtenir un accès.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
